/**
 * Credit note electronic document types (FatturaPA TD codes).
 * These document types invert the cash flow direction compared to regular invoices.
 */
export const CREDIT_NOTE_TYPES = ['TD04', 'TD08'];
/**
 * Debit note electronic document types (FatturaPA TD codes).
 * These document types follow the same cash flow direction as regular invoices.
 */
const DEBIT_NOTE_TYPES = ['TD05', 'TD09'];
/**
 * Autofattura (self-invoice) electronic document types (FatturaPA TD codes).
 * These are emitted to register foreign/intra-EU purchases. The totalAmount
 * includes Italian VAT, but the bank movement corresponds to the net amount
 * (taxableAmount) without VAT.
 */
export const AUTOFATTURA_TYPES = ['TD17', 'TD18', 'TD19'];
/**
 * Documenti che l'azienda emette VERSO SÉ STESSA: cedente e cessionario sono
 * lo stesso soggetto (il tenant) e non esiste una controparte.
 *
 *  - **TD27**, autoconsumo e cessioni gratuite senza rivalsa (art. 2 c. 2 n.
 *    4 e 5 DPR 633/72): la merce esce dall'azienda senza corrispettivo e
 *    l'IVA resta a debito di chi la cede;
 *  - **TD21**, autofattura per splafonamento del plafond dell'esportatore
 *    abituale (Ris. 16/E del 6 febbraio 2017).
 *
 * Su entrambi il SDI PRETENDE che le due parti coincidano (controllo
 * **00472**), ed è l'esatto contrario del 00471 che vale per tutti gli altri
 * codici. Il documento nasce quindi con `companyId = null`: una controparte
 * associata è un errore, non un dato mancante.
 */
export const SELF_ISSUED_TYPES = ['TD21', 'TD27'];
/**
 * Documenti emessi dall'azienda che NON muovono denaro per conto proprio.
 *
 * Con il FORNITORE come cedente:
 *
 *  - **TD16**, integrazione IVA di una fattura in inversione contabile
 *    INTERNA (artt. 17 c. 5-6 e 74 c. 7-8 DPR 633/72): il pagamento sta sulla
 *    fattura passiva del fornitore, che è già nel sistema;
 *  - **TD20**, autofattura per regolarizzazione e integrazione (art. 6 c.
 *    9-bis D.Lgs. 471/97 e art. 46 c. 5 DL 331/93): dichiara il solo
 *    imponibile non fatturato dal fornitore, e non chiude l'adempimento da
 *    solo — l'imposta la porta il documento emesso "a seguire" (TD16 nel
 *    reverse charge interno, TD17/18/19 negli acquisti intra-UE). Il
 *    pagamento al fornitore sta altrove, o non è ancora avvenuto: non spetta
 *    a questo documento;
 *  - **TD29**, comunicazione di omessa o irregolare fatturazione (art. 6 c. 8
 *    D.Lgs. 471/97): è una segnalazione all'Agenzia, non un'operazione —
 *    nessuna rilevanza IVA, nessuna detrazione, nessun pagamento;
 *  - **TD22** e **TD23**, estrazione di beni da un deposito IVA (art. 50-bis
 *    c. 6 e c. 4 lett. c) DL 331/93): l'imponibile è quello del documento di
 *    acquisto PRECEDENTE, che ha già il suo pagamento; l'imposta del TD22 è
 *    in inversione contabile (doppia annotazione, nessun esborso) e quella
 *    del TD23 la versa il gestore del deposito con un F24 intestato a chi
 *    estrae. Una rata qui farebbe nascere una scadenza per un pagamento che
 *    su questo documento non esiste.
 *
 * Verso SÉ STESSI:
 *
 *  - **TD21**, autofattura per splafonamento del plafond dell'esportatore
 *    abituale (Ris. 16/E/2017): l'imposta si versa con un F24 o si assolve in
 *    liquidazione periodica. Né l'una né l'altra è una scadenza di QUESTO
 *    documento — l'F24 ha il suo modulo, la liquidazione non è un documento —
 *    e il fornitore da cui si è splafonato è già stato pagato sulla sua
 *    fattura;
 *  - **TD27**, autoconsumo e cessioni gratuite senza rivalsa: l'IVA è a
 *    debito ed è un COSTO per l'azienda, non un credito verso qualcuno. Il
 *    documento è annotato nel solo registro delle fatture emesse: nessun
 *    incasso da attendere, nessuna scadenza da creare.
 *
 * I primi tre condividono con le autofatture estere la struttura del payload
 * — cedente = fornitore, cessionario = azienda — ma NON la semantica di
 * cassa: niente rate, niente scadenzario, previsionale, fatturato,
 * riconciliazione o commesse. Per questo NON vanno aggiunti ad
 * `AUTOFATTURA_TYPES`, che porta anche la regola "la rata vale l'imponibile".
 *
 * Il nome NON dice più "supplier issued": dal TD27 l'insieme contiene anche
 * un documento che di cedente non ha nessun fornitore.
 */
export const NO_CASH_FLOW_TYPES = [
    'TD16',
    'TD20',
    'TD21',
    'TD22',
    'TD23',
    'TD27',
    'TD29',
];
/**
 * Documenti emessi dall'azienda con un FORNITORE come cedente che sono
 * l'UNICO documento dell'acquisto e ne portano quindi il pagamento per
 * intero, IVA compresa.
 *
 * Oggi solo **TD28** (acquisti da San Marino con IVA su fattura cartacea,
 * DM 21/06/2021): la fattura di carta del fornitore sammarinese non entra nel
 * sistema, e l'IVA l'ha addebitata lui — quindi si paga il totale, non
 * l'imponibile come nelle autofatture estere dove l'imposta è autoliquidata.
 *
 * Verso del denaro: in USCITA, benché il documento sia OUTGOING per il SDI.
 */
export const SUPPLIER_INVOICED_WITH_VAT_TYPES = ['TD28'];
/**
 * Tipi OUTGOING per il SDI il cui denaro però ESCE: registrano un acquisto.
 * È l'insieme che governa `getDocumentFlowType` (e il filtro SQL gemello
 * della riconciliazione), non la base di calcolo delle rate — quella
 * distingue autofatture (imponibile) e TD28 (totale).
 */
export const PURCHASE_FLOW_SELF_ISSUED_TYPES = [
    ...AUTOFATTURA_TYPES,
    ...SUPPLIER_INVOICED_WITH_VAT_TYPES,
];
const SUPPLIER_ISSUED_KIND_BY_TYPE = {
    TD16: 'RC_INTEGRATION',
    TD17: 'FOREIGN_AUTOFATTURA',
    TD18: 'FOREIGN_AUTOFATTURA',
    TD19: 'FOREIGN_AUTOFATTURA',
    TD20: 'REGULARIZATION',
    TD22: 'VAT_WAREHOUSE_EXTRACTION',
    TD23: 'VAT_WAREHOUSE_EXTRACTION',
    TD28: 'SAN_MARINO_PURCHASE',
    TD29: 'OMITTED_INVOICE_COMMUNICATION',
};
/** Famiglia del documento a parti invertite, `null` per tutti gli altri. */
export function getSupplierIssuedKind(electronicType) {
    return electronicType
        ? (SUPPLIER_ISSUED_KIND_BY_TYPE[electronicType] ?? null)
        : null;
}
/**
 * Checks if the given electronicType represents a credit note.
 *
 * Credit notes (TD04, TD08) invert the cash flow:
 * - INCOMING credit note = we receive money back (INFLOW)
 * - OUTGOING credit note = we refund money (OUTFLOW)
 */
export function isCreditNote(electronicType) {
    return (!!electronicType &&
        CREDIT_NOTE_TYPES.includes(electronicType));
}
/**
 * Checks if the given electronicType represents a debit note.
 *
 * Debit notes (TD05, TD09) follow the same cash flow direction as regular invoices
 * (they represent an additional charge, not a reversal).
 */
export function isDebitNote(electronicType) {
    return (!!electronicType &&
        DEBIT_NOTE_TYPES.includes(electronicType));
}
/**
 * Checks if the given electronicType represents an autofattura (self-invoice).
 *
 * Autofatture (TD17, TD18, TD19) are emitted for foreign/intra-EU purchases.
 * Their totalAmount includes Italian VAT, but the bank movement corresponds
 * to the net amount (document.taxableAmount).
 */
export function isAutofattura(electronicType) {
    return (!!electronicType &&
        AUTOFATTURA_TYPES.includes(electronicType));
}
/**
 * `true` per un documento emesso dall'azienda SENZA effetto di cassa (TD16
 * integrazione, TD20 regolarizzazione, TD21 splafonamento, TD22 e TD23
 * estrazione da deposito IVA, TD27 autoconsumo, TD29 comunicazione).
 *
 * Niente rate, niente scadenzario, previsionale, fatturato, riconciliazione o
 * commesse. Chi lo consuma per costruire il payload FatturaPA usa invece
 * `isSupplierIssuedSelfInvoice` o `isSelfIssuedDocument`, che dicono CHI sono
 * le due parti.
 */
export function isNoCashFlowDocument(electronicType) {
    return (!!electronicType &&
        NO_CASH_FLOW_TYPES.includes(electronicType));
}
/**
 * `true` per un documento che l'azienda emette verso SÉ STESSA (TD21, TD27):
 * cedente e cessionario sono lo stesso soggetto e non c'è controparte.
 *
 * È il predicato del PAYLOAD, gemello di `isSupplierIssuedSelfInvoice`: da
 * qui discendono il ramo del mapper, quello del validator, la deroga al
 * controllo sulla controparte obbligatoria e le etichette della stampa.
 */
export function isSelfIssuedDocument(electronicType) {
    return (!!electronicType &&
        SELF_ISSUED_TYPES.includes(electronicType));
}
/**
 * `true` per un documento a parti invertite che è l'unico documento
 * dell'acquisto e porta il pagamento al fornitore IVA INCLUSA (TD28).
 */
export function isSupplierInvoicedWithVat(electronicType) {
    return (!!electronicType &&
        SUPPLIER_INVOICED_WITH_VAT_TYPES.includes(electronicType));
}
/**
 * `true` quando il denaro ESCE benché il documento sia OUTGOING per il SDI:
 * autofatture estere e acquisti da San Marino con IVA.
 */
export function hasPurchaseCashFlow(electronicType) {
    return (isAutofattura(electronicType) || isSupplierInvoicedWithVat(electronicType));
}
/**
 * `true` per ogni documento che l'azienda emette con un FORNITORE come
 * cedente: autofatture estere TD17/18/19, integrazione interna TD16,
 * regolarizzazione TD20, estrazione da deposito IVA TD22/TD23, acquisto da
 * San Marino TD28, comunicazione di omessa fatturazione TD29.
 *
 * È il predicato del PAYLOAD (cedente = controparte, cessionario = azienda) e
 * di tutto ciò che ne discende: mapper, pre-check, guard di invio, stampa,
 * etichette. Non dice nulla sul flusso di cassa.
 */
export function isSupplierIssuedSelfInvoice(electronicType) {
    return getSupplierIssuedKind(electronicType) !== null;
}
/**
 * `true` per ogni documento fiscale che l'azienda emette e che NON è una
 * vendita a un cliente: quelli con il fornitore come cedente (TD16, TD17,
 * TD18, TD19, TD20, TD22, TD23, TD28, TD29) e quelli verso sé stessi (TD21,
 * TD27).
 *
 * È l'ombrello che risponde alla domanda "questo documento è fatturato? è un
 * credito di vendita?" — e la risposta è sempre no. Le commesse usano
 * un criterio economico distinto: TD17/18/19 sono costi di acquisto.
 * Un solo predicato invece della stessa coppia di `||` ripetuta in ogni
 * aggregazione di vendita: KPI del cruscotto e piano rate.
 */
export function isNonSalesFiscalDocument(electronicType) {
    return (isSupplierIssuedSelfInvoice(electronicType) ||
        isSelfIssuedDocument(electronicType));
}
/**
 * Checks if the given electronicType represents any type of note (credit or debit).
 */
export function isNote(electronicType) {
    return isCreditNote(electronicType) || isDebitNote(electronicType);
}
/**
 * Determines the FlowType (INFLOW/OUTFLOW) based on document properties.
 *
 * Normal documents and debit notes:
 * - OUTGOING (sales invoice) = we receive money = INFLOW
 * - INCOMING (purchase invoice) = we pay money = OUTFLOW
 * - TD17/18/19 autofatture (foreign purchase integrations) and TD28 (San
 *   Marino purchase with VAT) register a purchase, so positive amounts are
 *   OUTFLOW even when the draft is OUTGOING for SDI sending.
 *
 * Credit notes (TD04, TD08) invert the flow:
 * - OUTGOING credit note = we refund customer = OUTFLOW
 * - INCOMING credit note = supplier refunds us = INFLOW
 *
 * Negative purchase self-issued documents (amount < 0) are always INFLOW:
 * they reverse a purchase regardless of the technical SDI direction.
 */
export function getDocumentFlowType(direction, electronicType, amount) {
    const isOutgoing = direction === 'OUTGOING';
    const creditNote = isCreditNote(electronicType);
    const purchaseFlow = hasPurchaseCashFlow(electronicType);
    const negativePurchase = purchaseFlow && amount !== undefined && amount < 0;
    if (negativePurchase)
        return 'INFLOW';
    if (creditNote) {
        return isOutgoing ? 'OUTFLOW' : 'INFLOW';
    }
    if (purchaseFlow) {
        return 'OUTFLOW';
    }
    return isOutgoing ? 'INFLOW' : 'OUTFLOW';
}
/**
 * Returns the amount with the correct sign based on document direction and type.
 *
 * - INCOMING (purchase) → negative (cash outflow)
 * - OUTGOING (sale) → positive (cash inflow)
 * - Credit notes invert the sign (INCOMING NC → positive, OUTGOING NC → negative)
 * - Debit notes follow normal sign (same as regular invoices)
 *
 * Zero amounts always return 0 (not -0).
 */
export function getSignedAmount(amount, direction, electronicType) {
    if (amount === 0)
        return 0;
    const flowType = direction
        ? getDocumentFlowType(direction, electronicType, amount)
        : 'INFLOW';
    return flowType === 'OUTFLOW' ? -Math.abs(amount) : Math.abs(amount);
}
//# sourceMappingURL=document-flow-type.js.map